import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    createInitiative,
    updateInitiative,
    deleteInitiatives,
    getInitiative,
    getInitiatives,
    getInitiativeCategories,
    getInitiativeKpiOptions,
    getInitiativeKpiUnits,
    getInitiativeTypes,
    getInitiativesOverview,
    CreateInitiativeDTO,
    UpdateInitiativeDTO,
    InitiativeTypeDTO,
    InitiativeCategoryDTO,
    InitiativeKpiDTO,
    InitiativeKpiUnitDTO,
    InitiativeListItemDTO,
    InitiativeDTO,
    InitiativeOverviewDTO,
} from 'api';
import { QUERY_KEYS } from 'consts';

export const useGetInitiatives = () =>
    useQuery<InitiativeListItemDTO[]>([QUERY_KEYS.INITIATIVES], getInitiatives);

export const useGetInitiative = (id: number) =>
    useQuery<InitiativeDTO>([QUERY_KEYS.INITIATIVES, id], () => getInitiative(id), {
        enabled: !!id,
    });

export const useInitiativeTypes = () =>
    useQuery<InitiativeTypeDTO[]>([QUERY_KEYS.INITIATIVE_TYPES], getInitiativeTypes);

export const useInitiativeCategories = () =>
    useQuery<InitiativeCategoryDTO[]>([QUERY_KEYS.INITIATIVE_CATEGORIES], getInitiativeCategories);

export const useInitiativeKpiOptions = () =>
    useQuery<InitiativeKpiDTO[]>([QUERY_KEYS.INITIATIVE_KPIS], getInitiativeKpiOptions);

export const useInitiativeKpiUnits = () =>
    useQuery<InitiativeKpiUnitDTO>([QUERY_KEYS.INITIATIVE_KPI_UNITS], getInitiativeKpiUnits);

export const useInitiativesOverview = () =>
    useQuery<InitiativeOverviewDTO>([QUERY_KEYS.INITIATIVE_OVERVIEW], getInitiativesOverview);

export const useCreateInitiative = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (body: CreateInitiativeDTO) => createInitiative(body),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.INITIATIVES]);
            },
        },
    );
};

export const useUpdateInitiative = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ id, body }: { id: number; body: UpdateInitiativeDTO }) => updateInitiative(id, body),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.INITIATIVES]);
            },
        },
    );
};

export const useDeleteInitiatives = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (ids: number[]) => deleteInitiatives(ids),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEYS.INITIATIVES]);
            },
        },
    );
};
